import React from 'react';

import * as Yup from 'yup';
import i18n from 'i18n-js';
import { Form, Formik } from 'formik';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import Button from '@material-ui/core/Button';
import { TextField, Typography, DialogActions, FormControl } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import { DishData, SectionData } from 'store/menuDetailsStore/reducer';
import { useStyles } from '../styles';
import { MenuType } from 'constants/enums';
import { useAppSelector } from 'index';
import { MenuData } from 'store/menusStore/reducer';

interface ChooseExistingFromCatalogModalProps {
    isVisible: boolean;
    onClose: () => void;
    onSubmit: (dish: DishData | null) => void;
    sectionId: string;
}

interface InitialValues {
    section: SectionData | undefined;
    catalog: MenuData | undefined;
    dish: DishData | null;
}

const validationSchema = Yup.object().shape({
    section: Yup.object()
        .required(i18n.t('form.errors.required', { name: i18n.t('common.section') }))
        .nullable(),
    catalog: Yup.object()
        .required(i18n.t('form.errors.required', { name: i18n.t('restaurant.catalog.one') }))
        .nullable(),
    dish: Yup.object()
        .required(i18n.t('form.errors.required', { name: i18n.t('common.dish') }))
        .nullable(),
});

export default function ChooseExistingFromCatalogModal(props: ChooseExistingFromCatalogModalProps) {
    const classes = useStyles();
    const menus = useAppSelector((state) => state.menus.menus);
    const menuDetails = useAppSelector((state) => state.menuDetails.menu);

    const initalFormValues: InitialValues = {
        section: menuDetails.sections.find((x) => x.id === props.sectionId),
        catalog: menus.find((x) => x.type === MenuType.Catalog),
        dish: null,
    };

    return (
        <>
            <Dialog open={props.isVisible} aria-labelledby="form-dialog-title">
                <DialogTitle id="form-dialog-title">
                    <Typography variant="h6" align="left" className={classes.chooseExistingTitle}>
                        {i18n.t('common.copyFromCatalog')}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Formik
                        initialValues={{ ...initalFormValues }}
                        validationSchema={validationSchema}
                        onSubmit={(values, { resetForm }) => {
                            const { section, dish } = values;
                            if (!!section && !!dish) {
                                props.onSubmit({ ...dish, sectionId: section.id });
                                resetForm();
                                props.onClose();
                            }
                        }}
                    >
                        {({ touched, errors, values, setFieldValue, isSubmitting, dirty }) => (
                            <Form className={classes.chooseExistingContainer}>
                                <>
                                    <Autocomplete
                                        id="section"
                                        options={menuDetails.sections}
                                        getOptionLabel={(option) => option.name}
                                        value={values.section}
                                        getOptionSelected={(option, value) =>
                                            option.id === value.id
                                        }
                                        onChange={(e: object, value: any | null) => {
                                            setFieldValue('section', value);
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                name="section"
                                                label={
                                                    menuDetails.type === MenuType.Daily
                                                        ? i18n.t('form.daylyAddPlaceholder')
                                                        : i18n.t('common.section')
                                                }
                                                variant="filled"
                                                fullWidth
                                                error={Boolean(touched.section && errors.section)}
                                                helperText={
                                                    touched.section && errors.section
                                                        ? errors.section
                                                        : ''
                                                }
                                            />
                                        )}
                                    />
                                </>
                                <FormControl fullWidth>
                                    <Autocomplete
                                        id="catalog"
                                        options={menus.filter((m) => m.type === MenuType.Catalog)}
                                        getOptionLabel={(option) => option.name}
                                        value={values.catalog || null}
                                        getOptionSelected={(option, value) =>
                                            option.id === value.id
                                        }
                                        onChange={(e: object, value: MenuData | null) => {
                                            setFieldValue('catalog', value);
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                name="catalog"
                                                label={i18n.t('restaurant.catalog.one')}
                                                variant="filled"
                                                fullWidth
                                                error={Boolean(touched.catalog && errors.catalog)}
                                                helperText={
                                                    touched.catalog && errors.catalog
                                                        ? errors.catalog
                                                        : ''
                                                }
                                            />
                                        )}
                                    />
                                </FormControl>
                                <>
                                    <Autocomplete
                                        id="dish"
                                        options={
                                            values.catalog?.sections
                                                ?.map((section) =>
                                                    section.dishes?.map((dish) => dish)
                                                )
                                                ?.flat() || []
                                        }
                                        getOptionLabel={(option) => option.name}
                                        renderOption={(o) => (
                                            <span style={o.isDisabled ? { color: 'grey' } : {}}>
                                                {o.name}
                                            </span>
                                        )}
                                        value={values.dish}
                                        getOptionSelected={(option, value) =>
                                            option.id === value.id
                                        }
                                        onChange={(e: object, value: any | null) => {
                                            setFieldValue('dish', value);
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                name="dish"
                                                label={i18n.t('common.dish')}
                                                variant="filled"
                                                fullWidth
                                                error={Boolean(touched.dish && errors.dish)}
                                                helperText={
                                                    touched.dish && errors.dish ? errors.dish : ''
                                                }
                                            />
                                        )}
                                    />
                                </>
                                <DialogActions>
                                    <Button type="button" color="primary" onClick={props.onClose}>
                                        {i18n.t('button.cancel')}
                                    </Button>
                                    <Button
                                        type="submit"
                                        disabled={
                                            !dirty || isSubmitting || Object.keys(errors).length > 0
                                        }
                                        variant="contained"
                                        color="primary"
                                    >
                                        {i18n.t('common.choose')}
                                    </Button>
                                </DialogActions>
                            </Form>
                        )}
                    </Formik>
                </DialogContent>
            </Dialog>
        </>
    );
}
